import { PaperClipOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { formatDate, formatDateTime } from '@shared/lib/format-date';
import { formatTiyinAsSum } from '@shared/lib/money';
import { Card, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import type { InstallmentAction } from '@api/generated/types.gen';

import { shortId } from '../api';

const { Text } = Typography;

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'default',
  MARKED_PAID: 'orange',
  PAID: 'success',
  OVERDUE: 'error',
};

type InstallmentsBlockProps = {
  installments: InstallmentAction[];
  actorPhones: Record<string, string>;
};

/**
 * Блок «График рассрочки»: позиции с отметками сторон.
 *
 * `MARKED_PAID` («заёмщик отметил, кредитор не подтвердил») подсвечен
 * оранжевым — это спорная зона. `PAID` без `confirmedBy` — авто-подтверждение
 * по молчанию кредитора: показываем «авто», а не пустую ячейку
 * (в ленте аудита ему соответствует `AUTO_CONFIRM_PAID` без актора).
 * Для разовых займов массив пуст и блок не рендерится (см. родителя).
 */
export function InstallmentsBlock({ installments, actorPhones }: InstallmentsBlockProps) {
  const { t } = useTranslation('agreements');

  const actorLabel = (id: string | undefined): string | null => {
    if (!id) return null;
    return actorPhones[id] ?? shortId(id);
  };

  return (
    <Card
      title={t('installments.title')}
      style={{ borderColor: designTokens.slate200 }}
      styles={{
        header: { borderBottomColor: designTokens.slate200, fontWeight: 600 },
        body: { padding: 0 },
      }}
    >
      {installments.map((item, idx) => {
        const isAuto = item.status === 'PAID' && !item.confirmedBy;
        const marked = actorLabel(item.markedBy);
        const confirmed = actorLabel(item.confirmedBy);
        return (
          <div
            key={item.seqNo}
            style={{
              padding: '10px 16px',
              borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '4px 12px',
              }}
            >
              <Text style={{ fontSize: 13, fontWeight: 600 }}>
                {t('installments.item', {
                  seqNo: item.seqNo,
                  amount: formatTiyinAsSum(item.amountTiyin),
                  dueDate: formatDate(item.dueDate),
                })}
              </Text>
              <span style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                {item.hasProof && (
                  <Tag style={{ fontSize: 11, marginInlineEnd: 0 }} icon={<PaperClipOutlined />}>
                    {t('installments.hasProof')}
                  </Tag>
                )}
                <Tag
                  color={STATUS_COLORS[item.status ?? ''] ?? 'default'}
                  style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                >
                  {t(`installments.statuses.${item.status ?? 'PENDING'}`, {
                    defaultValue: item.status,
                  })}
                </Tag>
              </span>
            </div>
            {(marked ?? confirmed ?? isAuto) && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '2px 16px',
                  marginBlockStart: 4,
                }}
              >
                {marked && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('installments.markedBy', {
                      who: marked,
                      when: formatDateTime(item.markedAt),
                    })}
                  </Text>
                )}
                {(confirmed ?? isAuto) && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {t('installments.confirmedBy', {
                      who: isAuto ? t('installments.auto') : confirmed,
                      when: formatDateTime(item.confirmedAt),
                    })}
                  </Text>
                )}
              </div>
            )}
          </div>
        );
      })}
    </Card>
  );
}
